import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Save, Plus, Trash2, Camera, Info, Loader2 } from "lucide-react";
import api, { apiErr, resolveImg } from "@/lib/api";
import MenuOrderList, { sortByOrder } from "@/pages/dashboard/MenuOrderList";

const smallInput = "w-full border border-slate-300 rounded-xl px-3 py-2 outline-none focus:border-cc-red text-sm";

function AvailToggle({ value, onChange, testid }) {
  return (
    <button type="button" data-testid={testid} onClick={() => onChange(!value)}
      className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${value ? "bg-green-100 text-green-700" : "bg-slate-200 text-slate-500"}`}>
      <span className={`w-2 h-2 rounded-full ${value ? "bg-green-500" : "bg-slate-400"}`} />
      {value ? "Disponible" : "Indisponible"}
    </button>
  );
}

function PhotoTips() {
  return (
    <div className="bg-cc-navy/5 border border-cc-navy/10 rounded-2xl p-4 text-sm text-slate-600">
      <p className="font-bold cc-navy flex items-center gap-2 mb-2"><Camera size={16} /> Réussir la photo de votre pizza</p>
      <ul className="space-y-1 list-disc pl-5">
        <li>Photographiez la pizza <strong>à la verticale, vue de dessus</strong> (à plat).</li>
        <li>Posez-la sur un <strong>fond clair et uni</strong> (assiette blanche, plan de travail clair).</li>
        <li>Lumière douce et régulière, <strong>évitez les ombres marquées</strong>.</li>
        <li>Cadrez la pizza <strong>entière et centrée</strong>, sans autres objets autour.</li>
      </ul>
      <p className="mt-2 text-slate-500">✨ La photo est <strong>détourée automatiquement</strong> : le fond est retiré et la pizza est ajoutée à votre carte.</p>
    </div>
  );
}

export default function PizzeriaMenu() {
  const [pizzas, setPizzas] = useState([]);
  const [prices, setPrices] = useState({});
  const [availability, setAvailability] = useState({});
  const [customs, setCustoms] = useState([]);
  const [descriptions, setDescriptions] = useState({});
  const [ingredients, setIngredients] = useState({});
  const [menuOrder, setMenuOrder] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const fileRefs = useRef({});

  const load = () => {
    api.get("/pizzas").then((r) => setPizzas(r.data));
    api.get("/pizzerias/mine").then((r) => {
      if (r.data?.prices) setPrices(r.data.prices);
      if (r.data?.availability) setAvailability(r.data.availability);
      if (r.data?.custom_pizzas) setCustoms(r.data.custom_pizzas);
      setDescriptions(r.data?.descriptions || {});
      setIngredients(r.data?.ingredients || {});
      setMenuOrder(r.data?.menu_order || []);
    });
  };
  useEffect(load, []);

  const isAvail = (id) => availability[id] !== false;
  const allItems = sortByOrder([...pizzas, ...customs.map((c) => ({ ...c, custom: true }))], menuOrder);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/pizzerias/mine/menu", { prices, availability, descriptions, ingredients, menu_order: allItems.map((p) => p.id), custom_pizzas: customs });
      toast.success("Votre carte a été enregistrée");
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const addCustom = () => setCustoms([...customs, { id: `new-${Date.now()}`, name: "", description: "", price: "", image_url: "", available: true }]);
  const updCustom = (i, patch) => setCustoms(customs.map((c, idx) => idx === i ? { ...c, ...patch } : c));
  const delCustom = (i) => setCustoms(customs.filter((_, idx) => idx !== i));

  const uploadPhoto = async (i, fileList) => {
    const f = fileList?.[0];
    if (!f) return;
    setUploadingId(customs[i].id);
    try {
      const fd = new FormData();
      fd.append("file", f, f.name || "pizza.jpg");
      const { data } = await api.post("/pizzerias/mine/pizza-photo", fd);
      updCustom(i, { image_url: data.url });
      toast.success("Photo détourée et ajoutée !");
    } catch (e) { toast.error(apiErr(e)); } finally { setUploadingId(null); }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Carte COCARDE */}
      <div className="card-cocarde p-6">
        <h3 className="font-display text-xl cc-navy">La carte COCARDE</h3>
        <p className="text-slate-500 text-sm mb-4">Fixez vos prix, indiquez ce que vous proposez et personnalisez la description et les ingrédients. Le nom et la photo restent ceux de la marque.</p>
        <div className="space-y-4">
          {pizzas.map((p) => (
            <div key={p.id} data-testid={`menu-cocarde-${p.id}`} className="border-b border-slate-100 pb-4 last:border-0 last:pb-0">
            <div className="flex items-center gap-3 flex-wrap">
              <img src={resolveImg(p.image_url)} alt={p.name} className="w-12 h-12 rounded-lg object-cover bg-slate-100" />
              <div className="flex-1 min-w-[120px]"><p className="font-bold cc-navy">{p.name}</p><p className="text-xs text-slate-400">Prix conseillé {p.price.toFixed(2)} €</p></div>
              <AvailToggle testid={`menu-avail-${p.id}`} value={isAvail(p.id)} onChange={(v) => setAvailability({ ...availability, [p.id]: v })} />
              <div className="relative">
                <input data-testid={`menu-price-${p.id}`} type="number" step="0.10" placeholder={p.price.toFixed(2)}
                  value={prices[p.id] ?? ""} onChange={(e) => setPrices({ ...prices, [p.id]: e.target.value })}
                  className="w-24 border border-slate-300 rounded-xl px-3 py-2 pr-6 outline-none focus:border-cc-red" />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400">€</span>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-2 mt-2">
              <textarea data-testid={`menu-desc-${p.id}`} rows={2} placeholder={p.description || "Description"} value={descriptions[p.id] ?? ""}
                onChange={(e) => setDescriptions({ ...descriptions, [p.id]: e.target.value })} className={smallInput} />
              <textarea data-testid={`menu-ingredients-${p.id}`} rows={2} placeholder="Ingrédients (ex : tomate, mozzarella, basilic)" value={ingredients[p.id] ?? ""}
                onChange={(e) => setIngredients({ ...ingredients, [p.id]: e.target.value })} className={smallInput} />
            </div>
            </div>
          ))}
        </div>
      </div>

      {/* Pizzas maison */}
      <div className="card-cocarde p-6">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
          <h3 className="font-display text-xl cc-navy">Mes pizzas maison</h3>
          <button data-testid="add-custom-pizza" onClick={addCustom} className="btn-navy text-sm py-2 px-4 inline-flex items-center gap-1"><Plus size={15} /> Ajouter une pizza</button>
        </div>
        <p className="text-slate-500 text-sm mb-4 flex items-center gap-1"><Info size={14} /> Vos créations apparaissent sur votre page et dans la commande.</p>
        <PhotoTips />

        <div className="space-y-4 mt-4">
          {customs.map((c, i) => (
            <div key={c.id} data-testid={`custom-pizza-${i}`} className="border border-slate-200 rounded-2xl p-4 flex gap-4 flex-wrap sm:flex-nowrap">
              <div className="shrink-0">
                <div className="w-28 h-28 rounded-xl bg-slate-100 grid place-items-center overflow-hidden relative">
                  {uploadingId === c.id ? (
                    <Loader2 className="animate-spin text-cc-red" />
                  ) : c.image_url ? (
                    <img src={resolveImg(c.image_url)} alt={c.name} className="w-full h-full object-contain" />
                  ) : (
                    <Camera className="text-slate-300" size={30} />
                  )}
                </div>
                <input ref={(el) => (fileRefs.current[c.id] = el)} type="file" accept="image/*" className="hidden"
                  data-testid={`custom-pizza-file-${i}`} onChange={(e) => uploadPhoto(i, e.target.files)} />
                <button type="button" data-testid={`custom-pizza-photo-btn-${i}`} onClick={() => fileRefs.current[c.id]?.click()}
                  className="mt-2 w-28 text-xs font-bold text-cc-red border border-cc-red rounded-full py-1.5 inline-flex items-center justify-center gap-1 hover:bg-cc-red hover:text-white transition-colors">
                  <Camera size={12} /> {c.image_url ? "Changer" : "Photo"}
                </button>
              </div>

              <div className="flex-1 min-w-[180px] space-y-2">
                <div className="flex items-center gap-2">
                  <input data-testid={`custom-pizza-name-${i}`} placeholder="Nom de la pizza" value={c.name} onChange={(e) => updCustom(i, { name: e.target.value })}
                    className="flex-1 border border-slate-300 rounded-xl px-3 py-2 outline-none focus:border-cc-red font-bold" />
                  <AvailToggle testid={`custom-pizza-avail-${i}`} value={c.available !== false} onChange={(v) => updCustom(i, { available: v })} />
                </div>
                <textarea data-testid={`custom-pizza-desc-${i}`} placeholder="Description" value={c.description} onChange={(e) => updCustom(i, { description: e.target.value })} rows={2}
                  className={smallInput} />
                <input data-testid={`custom-pizza-ingredients-${i}`} placeholder="Ingrédients" value={c.ingredients || ""} onChange={(e) => updCustom(i, { ingredients: e.target.value })}
                  className={smallInput} />
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <input data-testid={`custom-pizza-price-${i}`} type="number" step="0.10" placeholder="Prix" value={c.price} onChange={(e) => updCustom(i, { price: e.target.value })}
                      className="w-28 border border-slate-300 rounded-xl px-3 py-2 pr-6 outline-none focus:border-cc-red" />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400">€</span>
                  </div>
                  <button type="button" data-testid={`custom-pizza-del-${i}`} onClick={() => delCustom(i)} className="ml-auto text-red-500 hover:bg-red-50 rounded-lg p-2"><Trash2 size={16} /></button>
                </div>
              </div>
            </div>
          ))}
          {customs.length === 0 && <p className="text-slate-400 text-sm">Aucune pizza maison. Cliquez sur « Ajouter une pizza ».</p>}
        </div>
      </div>

      <MenuOrderList items={allItems} onChange={setMenuOrder} />

      <button data-testid="menu-save" onClick={save} disabled={busy} className="btn-cocarde inline-flex items-center gap-2 disabled:opacity-60"><Save size={16} /> {busy ? "Enregistrement…" : "Enregistrer ma carte"}</button>
    </div>
  );
}
